import { useEffect, useState } from 'react';
import { getMyDataServiceAccess } from '../../Services/DataService';
import { MyDataServiceAccess } from '../../Types/DataServiceType';

/**
 * Whether to show a Data Service link in a recruiter or candidate navigation.
 *
 * <p>The link is driven by the server, not by the role. A recruiter and a
 * candidate are both configured roles, but until something has actually been
 * configured for them they have nothing to open, and a permanently visible link
 * that always lands on an empty page reads as a broken feature rather than as
 * "you do not have access yet".</p>
 *
 * <p>Defaults to hidden and only turns true once the server says so, so the
 * link never flashes in for a user who cannot use it.</p>
 */
export const useDataServiceAccess = () => {
  const [access, setAccess] = useState<MyDataServiceAccess | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await getMyDataServiceAccess();
        if (!cancelled && res.success && res.data) setAccess(res.data);
      } catch {
        // Leave it hidden: a failed check must not produce a link that 403s.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    hasDataServiceAccess: access?.hasAccess ?? false,
    connectionCount: access?.connectionCount ?? 0,
    /**
     * Per-person surface grants, used to decide which tabs to show.
     *
     * <p>Defaulted to true because this hook only feeds navigation: a super
     * admin and a delegate are data-service roles and keep every tab, and for
     * them the server is not the authority on what they may open. For a
     * configured recruiter or candidate the server is, and a hidden tab is a
     * convenience only - the endpoints enforce the same rule.</p>
     */
    queryBuilderAvailable: access?.queryBuilderAvailable ?? true,
    consoleAvailable: access?.consoleAvailable ?? true,
  };
};
